import { KeyDict, TalkbackMode } from '@/types'
import { UserProfile } from '@/types/users'
import { Bot } from '@/types/bots'
import { TranscodingRegion, CastProperty, IOSSettings, LegalInfo, LibratoConfig, Notifications } from '@/types/admin'
import { WarningMessage } from '@/classes/Admin/WarningMessage'
import { Team, MemberInvite, TeamGroup } from '@/types/teams'
import { Scene } from '@/types/scenes'
import { Layout } from '@/types/layouts'
import { Billing } from '@/types/billing'
import { Stream, SourceStream, StreamDoc, DefaultStream, CastStream } from '@/types/streams'
import { Cast, PlaylistStatus, AudioMixType, CastSummary, CastChatMessage } from '@/types/casts'
import { TranscodingStats, TranscodingMachine, SocialMediaStats, CefStat, BackupOverview, NGCVPMachineStats, GlobalAccelerator, SystemStatus } from '@/types/ops'
import { LogMessage, ClientInfo } from '@/types/logs'
import { VideoPlayer, VideoPlayerLocalState, VideoPlayerLastClicked } from '@/types/videoplayer'
import { Asset, AssetGroup, AssetSummary } from '@/types/assets'
import { CloudCastEvent } from '@/types/events'
import { ChatWidget } from '@/types/chatwidget'
import { CastTemplate } from '@/types/casttemplates'
import { firestore } from 'firebase/app'
import { NetworkstatsState } from "@/store/modules/networkstats/networkstats.state";
import { AbilitySubject, AbilityAction, TeamRole } from '@/abilitiesByRole'
import { WowzaStat } from '@/types/wowza'
import { Feature } from '@/featureFlags/featureFlags'
import FirebasePresence from '@/classes/FirebasePresence'

export const WATERMARK_DEFAULT = false

export type UnsubscribeFunction = () => void

export interface UserState {
  userLoggedIn: boolean,
  online: boolean,
  firebaseLogin: boolean,
  loginComplete: boolean,
  teamCheck: boolean|null,
  userHasNoTeams: boolean,
  email: string,
  id: string,
  teamId: string | null,
  profile: UserProfile | null,
  teamRole: string,
  displayName?: string
  notification?: string
  showProfile: boolean,
  needRepublish: boolean,
  defaultPageOnLogin: string | null,
  teams: Team[],
  legalAgreements: {
    tosVersion: number,
    privacyVersion: number,
    lastUpdateDialog: number | null,
  },
  presence: FirebasePresence | null
  isDumping: boolean
}

export interface BotsState {
  bots: KeyDict<Bot>
}

export class Ordway {
  entities : KeyDict<string>
  customers : KeyDict<KeyDict<KeyDict<string>>>
  constructor (data: firestore.DocumentData) {
    this.entities = {}
    if (data.entities !== undefined) {
      this.entities = data.entities
    }
    this.customers = {}
    if (data.customers !== undefined) {
      this.customers = data.customers
    }
  }
}
export interface AdminState {
  transcodingregions: KeyDict<TranscodingRegion>,
  iOS: IOSSettings,
  release: Release | null,
  legalinfo: LegalInfo,
  librato: LibratoConfig | null,
  featureFlags: Map<Feature, boolean>,
  ordway: Ordway | null,
  notifications: Notifications | null
}

export interface Release {
  default: string,
  versions: KeyDict<Version>
}

export interface ServiceInfo {
  running: boolean,
  last_changed_timestamp: firebase.firestore.Timestamp
}
export interface Version {
  id: string
  branch_front: string,
  branch_back: string,
  commit_back?: string,
  deployment_date_back?: firebase.firestore.Timestamp,
  machine_back?: string,
  deploy_firebase: {
    functions: boolean,
    rules: boolean
    manifests: boolean
  },
  services_info?: KeyDict<ServiceInfo>,
  services_enabled: KeyDict<boolean>,
  ngcvp_version?: string
}

export interface ArchiverTask {
  [id: string]: {
    k360event: number,
    mode: string,
    stream?: string,
    progress_pct?: number
  }
}

export interface OpsMonitorState {
  pendingSupportRequests: Cast[],
  streams: { [streamId: string]: Stream },
  srtstreams: { [streamId: string]: Stream }
  users: KeyDict<UserProfile>,
  socialMediaMonitor: SocialMediaStats | null,
  transcodingStats: KeyDict<TranscodingStats>,
  castMachineStats: NGCVPMachineStats,
  cefStats: KeyDict<CefStat>,
  transcodingMachines: KeyDict<TranscodingMachine>,
  archiverQueue: KeyDict<ArchiverTask>,
  archivingCasts: KeyDict<Cast>,
  brokenArchivedCasts: KeyDict<Cast>
  backupOverview: BackupOverview,
  wowzastats: KeyDict<WowzaStat>,
  opsmonitorcasts: KeyDict<Cast>,
  globalAccelerator: GlobalAccelerator
}

export class VideoPlayerState {
  videoplayers: KeyDict<VideoPlayer> = {}
  lastClicked: KeyDict<KeyDict<VideoPlayerLastClicked>> = {}
  localStates: KeyDict<KeyDict<VideoPlayerLocalState>> = {}
  currentSelectedClip: KeyDict<string | null> = {}
  videoPlayerStatus: KeyDict<SystemStatus> = {}
}

export interface TeamState {
  activeTeam: string,
  team: Team,
  currentTeam: UserProfile[],
  teamScenes: { [sceneId: string]: Scene },
  teamLayouts: Layout[],
  streams: { [streamId: string]: Stream },
  castProperties: { [id: string]: CastProperty },
  teamGroups: TeamGroup[] | undefined,
  warningMessages: KeyDict<WarningMessage>,
  allTeams: Team[],
  invites: MemberInvite[],
  defaultTeamLayout: string,
  teamLoaded: boolean,
  teamSize: number,
  defaultStreams: DefaultStream[],
  billing: Billing,
  version: string
}

export interface LogsState {
  clientInfo: ClientInfo,
  messages: LogMessage[]
}

export interface EventsState {
  inCast: boolean,
  actionPalleteTab: ActionPalleteTabs,
  simpleMode: boolean,
  currentCast: Cast,
  currentPlaylist: PlaylistStatus,
  eventMessages: CastChatMessage[],
  onlineUsers: string[],
  events: CloudCastEvent[],
  completedEvents: CloudCastEvent[],
  allevents: CloudCastEvent[],
  eventsLoaded: boolean,
  completedEventsLoaded: boolean,
  watermark?: boolean, // gets watermarked output etc
  assets: Asset[],
  sceneInPreview: string,
  cefMixer: string,
  cefEvent: boolean,
  cefMode: string,
  openScene: string,
  sceneFilter: string,
  currentCasterStream: string,
  localCameraStream: null | MediaStream,
  sourceStreams: { [streamId: string]: SourceStream },
  casting: boolean,
  outputStreams: { [streamId: string]: Stream },
  audioMix: AudioMixType,
  liveEditVolumes: KeyDict<number>,
  programVolumes: { [id: string]: number },
  monitorVolumes: { [id: string]: number },
  monitorMutes: { [id: string]: boolean },
  talkbackVolumes: { [id: string]: number },
  talkbackMutes: { [id: string]: boolean },
  talkbackMode: TalkbackMode,
  talkbackGlobalMute: boolean,
  talkbackGlobalVolume: number,
  talkBack: { [id: string]: string },
  customStreams: { [streamId: string]: Stream },
  streamDocs: { [streamId: string]: StreamDoc },
  castTeamDoc: Team,
  castTeam: UserProfile[],
  castingAs: string,
  castEnded: boolean,
  streamNames: { [streamId: string]: string },
  chatOpen: boolean,
  chatLastSeen: number,
  selfMute: boolean,
  fullscreen: boolean,
  castReady: boolean,
  createCastLargeMachine: boolean,
  activeCasts: KeyDict<CastSummary>,
  casterNames: KeyDict<string>,
  streamImages: KeyDict<string>,
  audioInit: boolean,
  isCaster: boolean|null,
  iosFront: boolean,
  isPreviewingOutput: boolean,
}

export interface clockState {
  minute: number
}

export interface ChatWidgetState {
  id: null | string,
  widget: ChatWidget
}

export class AssetsState {
  initializing: boolean  = false
  folderAssetsLoading: boolean = false
  allAssets: KeyDict<Asset> = {}
  sceneAssets: KeyDict<Asset> = {}
  assetGroupList: KeyDict<KeyDict<AssetSummary>> = {}
  assetGroupAssets: KeyDict<KeyDict<Asset>> = {}
  subFolders: KeyDict<AssetGroup> = {}
  folderList: KeyDict<AssetGroup> = {}
  selectedFolder: string = ''
  openFolders: string[] = []
}

export interface StreamsState {
  castStreams: KeyDict<CastStream>
  talkbackTargetFallback: string
  talkbackTargetCaster: string
}

export interface LayoutsState {
}

export interface AbilitiesState {
  teamRoles: KeyDict<TeamRole>,
  teamRolesReady: boolean
}

export interface CastTemplatesState {
  templates: CastTemplate[]
}

export interface RootState {
  admin: AdminState,
  user: UserState,
  team: TeamState,
  opsmonitor: OpsMonitorState,
  logs: LogsState,
  events: EventsState,
  clock: clockState,
  chatwidget: ChatWidgetState,
  assets: AssetsState,
  videoplayer: VideoPlayerState,
  layouts: LayoutsState,
  networkStats: NetworkstatsState,
  streams: StreamsState,
  bots: BotsState,
  abilities: AbilitiesState
}

export type DocMutation = (doc: firestore.DocumentSnapshot) => void
export type QueryMutation = (doc: firestore.QuerySnapshot) => void

export enum FirestoreSnapperType {
  Query = 'query',
  Document = 'document'
}

export enum ActionPalleteTabs {
  Assets = 'Assets',
  Inputs = 'Inputs',
  Casters = 'Casters',
  Outputs = 'Outputs',
  Audio = 'Audio Mixer',
  Clips = 'Clips',
  MyHeadphones = 'My Headphones',
  Talkback = 'Talkback',
  Interactive = 'Interactive',
  Chat = 'TextCast',
  SimpleCG = 'Simple CG'
}

export interface ActionTabDefinition {
  label: ActionPalleteTabs,
  value: number,
  panel: string,
  icon: string,
  component: any,
  action: AbilityAction
  allow: AbilitySubject | undefined
}

export interface FirestoreSnapper {
  readonly type: FirestoreSnapperType
}

export interface FirestoreDocSnapper extends FirestoreSnapper {
  doc: firestore.DocumentReference,
  mutation: DocMutation,
  readonly type: FirestoreSnapperType.Document
}

export interface FirestoreQuerySnapper extends FirestoreSnapper {
  query: firestore.Query,
  mutation: QueryMutation,
  readonly type: FirestoreSnapperType.Query
}

export interface Subscription {
  count: number,
  destroy: UnsubscribeFunction | null,
  snapper: FirestoreSnapper | null
}
